import { useMemo } from "react";

import {
  VoluntaryLifeBenefitDetails,
  GeneralInformation,
  PlanRates,
  VoluntaryLifeCoverage,
  VoluntaryLifePlan,
} from "../../constants/aquafina";
import CollapsableSection from "../sections/collapsable-section";
import { RangeIterator } from "../../utils";
import ContributionType from "../sections/contribution-type-section";
import { CoverageRow, CoverageHeaderRow, NotProvidedRow } from "../table/rows";
import { CoverageVectorHeaderCell, CoverageVectorCell } from "../table/cells";
import Rates from "../rates";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

type Props = {
  viewMode: ViewMode;
  displayV4Fields: boolean;
  coverage: VoluntaryLifeCoverage;
};

type BenefitState = {
  ee?: VoluntaryLifeBenefitDetails;
  sp?: VoluntaryLifeBenefitDetails;
  ch?: VoluntaryLifeBenefitDetails;
};

function VoluntaryLife({ coverage, viewMode, displayV4Fields }: Props) {
  const state = useMemo(() => {
    const benefitDetails: BenefitState[] = [];
    const generalInfo: GeneralInformation[] = [];
    const rates: PlanRates[] = [];
    const plans: VoluntaryLifePlan[] = [];
    let inconsistentRateData: boolean = false;

    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      plans.push(plan);
      generalInfo.push(plan.generalInformation);
      rates.push(plan.planRates);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
      const benefits = plan.benefitDetails;
      const benefitDetail: BenefitState = {};
      if (benefits.employee) {
        benefitDetail.ee = benefits.employee;
        benefitDetail.ee.amountNotToExceed = "-";
      }
      if (benefits.spouse) {
        benefitDetail.sp = benefits.spouse;
      }
      if (benefits.child) {
        benefitDetail.ch = benefits.child;
      }
      benefitDetails.push(benefitDetail);
    });

    return {
      benefitDetails,
      generalInfo,
      plans,
      rates,
      inconsistentRateData,
    };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo data={state.generalInfo} />
      )}

      <BenefitDetails
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
}

type BenefitsProps = {
  benefits: BenefitState[];
  displayV4Fields: boolean;
};

const BenefitDetails = ({ benefits, displayV4Fields }: BenefitsProps) => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <BenefitDetailsData benefits={benefits} displayV4Fields={displayV4Fields} />
  </CollapsableSection>
);

const buildHeader = (benefit: BenefitState) => {
  const headers: string[] = [];
  if (benefit.ee) {
    headers.push("Employee");
  }
  if (benefit.sp) {
    headers.push("Spouse");
  }
  if (benefit.ch) {
    headers.push("Child");
  }
  return headers;
};

const buildVectorValues = (
  benefit: BenefitState,
  field: keyof VoluntaryLifeBenefitDetails,
) => {
  const values: string[] = [];
  if (benefit.ee) {
    values.push(benefit.ee[field]);
  }
  if (benefit.sp) {
    values.push(benefit.sp[field]);
  }
  if (benefit.ch) {
    values.push(benefit.ch[field]);
  }
  return values;
};

const BenefitDetailsData = ({ benefits, displayV4Fields }: BenefitsProps) => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  return (
    <>
      <CoverageHeaderRow isWhite={true}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorHeaderCell
            key={index}
            label={buildHeader(benefits[index])}
          />
        ))}
      </CoverageHeaderRow>
      <CoverageRow label="Benefit Description">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "benefitDescription")}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Benefit Maximum" isWhite={true}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "benefitMaximum")}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Guarantee Issue">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "guaranteeIssue")}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow
        label="Dependent Election Amount Not to Exceed"
        isWhite={true}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "amountNotToExceed")}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <CoverageRow label="Definition of Earnings">
          {RangeIterator(benefits.length, (index) => (
            <CoverageVectorCell
              value={buildVectorValues(benefits[index], "definitionOfEarnings")}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
      <CoverageRow label="AD&D Included" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "adndIncluded")}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow
        label="AD&D Tied to Life Election"
        isWhite={!displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "adndTiedToLifeElection")}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Portability" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "portability")}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Age Reduction Schedule" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={buildVectorValues(benefits[index], "ageReductionSchedule")}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Conversion" isWhite={displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={buildVectorValues(
                  benefits[index],
                  "isConversionIncluded",
                )}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow
            label="Accelerated Death Benefit"
            isWhite={!displayV4Fields}
          >
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={buildVectorValues(
                  benefits[index],
                  "isAcceleratedDeathBenefitIncluded",
                )}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Waiver of Premium" isWhite={displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={buildVectorValues(
                  benefits[index],
                  "isWaiverOfPremiumIncluded",
                )}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow
            label="Annual Open Enrollment"
            isWhite={!displayV4Fields}
          >
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={buildVectorValues(
                  benefits[index],
                  "annualOpenEnrollment",
                )}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
    </>
  );
};

export default VoluntaryLife;
